import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { mutateConfig } from '../api/queryBase';
import { FormikHelpers, Formik, Field, Form } from 'formik';
import { AxiosError } from 'axios';
import { getErrorMessage, openNotification } from '../utils/helper';
import Input from '../components/input/Input';
import Button from '../components/button/Button';
import { Alert } from 'antd';

interface ForgotPasswordValues {
    email: string;
}

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const initialValues: ForgotPasswordValues = {
        email: ''
    };

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email('Invalid email address')
            .required('Email is required')
    });

    const mutation = mutateConfig();
    const { mutate, isLoading } = mutation({
        url: `/auth/password/forgot`
    });

    const handleSubmit = async (
        values: ForgotPasswordValues,
        {
            setSubmitting,
            setErrors,
            resetForm
        }: FormikHelpers<ForgotPasswordValues>
    ) => {
        mutate(values, {
            onSettled: (_, error: AxiosError | undefined | any) => {
                setSubmitting(true);
                setSuccessMessage(null);

                if (error) {
                    setSubmitting(false);
                    if (error.response?.data) {
                        const errors = error.response.data.error;
                        setErrors(errors);
                    } else {
                        openNotification(
                            'error',
                            getErrorMessage(error) ||
                                'Forgot password, Something went wrong'
                        );
                    }
                    return;
                }

                setSubmitting(false);
                setSuccessMessage(
                    'Forgot password, Check your email for the reset link.'
                );
                resetForm();
            }
        });
    };

    return (
        <div>
            <div className='bg-appcolorwhite py-[2.7rem] lg:py-[2.5rem] lg:px-[2.6rem] px-[1.6rem] mb-[1.75rem] rounded-[3px]'>
                {successMessage && (
                    <Alert
                        type='success'
                        banner
                        style={{
                            marginBottom: '1rem',
                            color: '#12B76A'
                        }}
                        className=' text-appcolorgreen'
                        message={successMessage}
                    />
                )}

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, errors, isValid }) => (
                        <Form autoComplete='off'>
                            <div>
                                <Field
                                    as={Input}
                                    isError={errors.email}
                                    errorMessage={errors.email}
                                    fullWidth
                                    name='email'
                                    type='email'
                                    label='Email'
                                />
                            </div>

                            <div className='mt-4'>
                                <Button
                                    isLoading={isSubmitting || isLoading}
                                    type='submit'
                                    className='w-full'
                                    disabled={
                                        !isValid || isSubmitting || isLoading
                                    }
                                    text='Reset Password'
                                />
                            </div>
                        </Form>
                    )}
                </Formik>

                <p className='mt-4 text-sm text-appcolorfaint leading-[18px]'>
                    Remember Password?{' '}
                    <span
                        className='underline cursor-pointer text-primary500'
                        onClick={() => {
                            navigate('/login');
                        }}
                    >
                        Log in
                    </span>
                </p>
            </div>
        </div>
    );
};

export default ForgotPassword;
